import { downloadCsv, exportTableToPdf } from './reportExport';
import { brandingFromUser } from './tenantBranding';
import { expenseCategoryLabelForUser } from '../constants/factoryExpenseCategories';
import { expensePaymentLabel } from '../constants/expensePayment';
import { staffPayTypeLabel } from '../constants/staffPayTypes';
import { expenseLang } from './expenseI18n';

const CSV_HEADERS = [
  'Date',
  'Category',
  'Description',
  'Amount (TZS)',
  'Pay type',
  'Cement bags',
  'Rate/bag',
  'Payment',
  'Reference',
  'Recorded by',
  'Notes',
  'SMS',
  'Has receipt',
];

export function expenseToCsvRow(row, user, categoryOverride, lang) {
  const l = lang || expenseLang(user);
  const cat = categoryOverride || expenseCategoryLabelForUser(user, row.category, l);
  return [
    row.expenseDate || '',
    cat,
    row.description || '',
    Number(row.amount) || 0,
    row.staffPayType ? staffPayTypeLabel(row.staffPayType, l) : '',
    row.cementBags != null ? row.cementBags : '',
    row.cementBagRateTzs != null ? row.cementBagRateTzs : '',
    expensePaymentLabel(row.paymentMethod, l),
    row.paymentReference || '',
    row.createdBy || '',
    row.notes || '',
    row.smsMessage ? String(row.smsMessage).replace(/\s+/g, ' ').trim() : '',
    row.evidenceUrl ? 'Yes' : '',
  ];
}

function buildPdfTable(expenses, user, options = {}) {
  const lang = options.lang || expenseLang(user);
  const includeCategory = options.includeCategory !== false;
  const headers = includeCategory
    ? ['Date', 'Category', 'Description', 'Amount (TZS)', 'Payment', 'By']
    : ['Date', 'Description', 'Amount (TZS)', 'Payment', 'By'];
  const rows = expenses.map((e) => {
    const cat = options.categoryLabel || expenseCategoryLabelForUser(user, e.category, lang);
    const cells = [
      e.expenseDate || '',
      ...(includeCategory ? [cat] : []),
      e.description || '',
      Number(e.amount || 0).toLocaleString('en-TZ'),
      expensePaymentLabel(e.paymentMethod, lang),
      e.createdBy || '',
    ];
    return cells;
  });
  return { headers, rows };
}

export function downloadExpensesCsv(expenses, user, options = {}) {
  const lang = options.lang || expenseLang(user);
  const list = Array.isArray(expenses) ? expenses : [];
  const rows = [CSV_HEADERS, ...list.map((e) => expenseToCsvRow(e, user, options.categoryLabel, lang))];
  const stamp = options.dateStamp || new Date().toISOString().slice(0, 10);
  const filename = options.filename || `expenses_${stamp}.csv`;
  downloadCsv(filename, rows, {
    branding: brandingFromUser(user),
    title: options.title || 'Expenses',
    subtitle: options.subtitle || '',
  });
}

export async function downloadExpensesPdf(expenses, user, options = {}) {
  const list = Array.isArray(expenses) ? expenses : [];
  const { headers, rows } = buildPdfTable(list, user, options);
  const total = list.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  await exportTableToPdf(options.title || 'Expenses', headers, rows, {
    branding: brandingFromUser(user),
    subtitle: options.subtitle || '',
    criteria: options.criteria || [],
    summary: [
      { label: 'Records', value: String(list.length) },
      { label: 'Total (TZS)', value: total.toLocaleString('en-TZ') },
    ],
  });
}

export function downloadTextFile(filename, text) {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function downloadUrlAsFile(url, filename) {
  if (!url) return;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('Could not download file.');
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename || 'download';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}
